import type { ColumnType, MenuProps } from "../../../utils/local-antd";

import React from "react";
import { TFunction } from "i18next";
import { shallowEqual } from "react-redux";
import { memo, useMemo } from "react";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { faEllipsis } from "@fortawesome/free-solid-svg-icons/faEllipsis";
import { faCircleUser } from "@fortawesome/free-solid-svg-icons/faCircleUser";

import { Text } from "../../../components/Text";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { EntityIcons } from "../../../utils/GlobalIcons";
import { Flex } from "../../../components/Flex";
import { AtiraDropdown } from "../../../components/AtiraDropdown";
import { Button } from "../../../components/Button";
import { ImageCreateEdit } from "../../../components/ImageCreateEdit";
import { UserProfileWrapper } from "../../../components/UserProfileWrapper";
import { TextWithTruncated } from "../../../components/TextWithTruncated";
import { GetReportsResponseDto } from "../../../model/reports/dto/GetReportsResponseDto";

type Data = GetReportsResponseDto["metricsByUser"][number];

type FNType = ATVoidFunction<Data>;

type FieldFunctions = {
  onDetails: FNType;
  t: TFunction;
};

type ActionsDropdownProps = {
  t: TFunction;
  onDetails: FNType;
};

const getActionMenuItems = (props: {
  t: TFunction;
  onDetails: FNType;
}): MenuProps["items"] => {
  const { onDetails, t } = props;
  return [
    {
      key: "table-g1",
      type: "group",
      children: [
        {
          label: (
            <Flex alignItems="center" gap="s">
              <AtiraIcon icon={faEye} color="main" />
              <Text color="main">{t("common.preview")}</Text>
            </Flex>
          ),
          onClick: onDetails as any,
          valid: true,
        },
      ]
        .filter((item) => item.valid !== false)
        .map((child, index) => ({
          ...child,
          key: index.toString(),
          style: { padding: 4 },
        })),
    },
  ];
};

const ActionsDropdown: React.FC<ActionsDropdownProps> = memo(
  ({ t, onDetails }) => {
    const items = useMemo(
      () => getActionMenuItems({ t, onDetails }),
      [t, onDetails],
    );

    return (
      <AtiraDropdown
        items={items}
        placement="bottomLeft"
        trigger={["click"]}
        destroyOnHidden
      >
        <Button
          icon={faEllipsis}
          margin="none"
          color="black"
          backgroundColor="white"
        />
      </AtiraDropdown>
    );
  },
);

const getFieldWidth = (field: string) => {
  switch (true) {
    case ["actions"].includes(field):
      return 50;
    case field === "assignee":
      return 220;
    default:
      return 200;
  }
};

/** A numeric column: same layout for every metric, only icon and field differ. */
const metricColumn = (params: {
  field: string;
  title: string;
  icon: typeof EntityIcons.Entries;
  pick: (row: Data) => number;
}): ColumnType<Data> & { data: string } => ({
  title: params.title,
  key: `table-${params.field}`,
  render: (row: Data) => (
    <Flex alignItems="center" gap="s">
      <AtiraIcon icon={params.icon} />
      <Text>{params.pick(row) ?? 0}</Text>
    </Flex>
  ),
  sorter: (a: Data, b: Data) => params.pick(a) - params.pick(b),
  data: params.field,
  onCell: () => ({ style: { minWidth: getFieldWidth(params.field) } }),
  shouldCellUpdate(record, prevRecord) {
    return !shallowEqual(record, prevRecord);
  },
});

const FieldRenderer = (
  column: string,
  functions: FieldFunctions,
): ColumnType<Data> & { data: string } => {
  const field = column;
  const { t, onDetails } = functions;

  switch (true) {
    case field === "actions":
      return {
        title: t("common.actions"),
        key: "table-actions",
        render: (row: Data) => (
          <Flex justifyContent="center">
            <ActionsDropdown t={t} onDetails={() => onDetails(row)} />
          </Flex>
        ),
        onCell: () => ({ style: { minWidth: getFieldWidth(field) } }),
        fixed: "left",
        data: column,
        shouldCellUpdate(record, prevRecord) {
          return !shallowEqual(record, prevRecord);
        },
      };

    case field === "assignee":
      // Rendered from the report's own actor fields rather than AssigneeBanner,
      // which hides the logged-in user and so left the owner's own row blank.
      return {
        title: t("common.members"),
        key: "assignee",
        render: ({ name, picture }: Data) => (
          <Flex alignItems="center" gap="s">
            {picture ? (
              <UserProfileWrapper>
                <ImageCreateEdit src={picture} sizes="" />
              </UserProfileWrapper>
            ) : (
              <AtiraIcon icon={faCircleUser} color="main" size="xl" />
            )}
            <TextWithTruncated length={25}>{name}</TextWithTruncated>
          </Flex>
        ),
        sorter: (a: Data, b: Data) =>
          (a.name || "").localeCompare(b.name || ""),
        data: column,
        fixed: "left",
        onCell: () => ({ style: { minWidth: getFieldWidth(field) } }),
        shouldCellUpdate(record, prevRecord) {
          return !shallowEqual(record, prevRecord);
        },
        ellipsis: true,
      };

    case field === "entries":
      return metricColumn({
        field,
        title: t("common.entriesConvertedByUser"),
        icon: EntityIcons.Entries,
        pick: (row) => row.entriesConverted,
      });

    case field === "deals":
      return metricColumn({
        field,
        title: t("common.dealsClosedByUser"),
        icon: EntityIcons.Deals,
        pick: (row) => row.dealsClosed,
      });

    case field === "tasks":
      return metricColumn({
        field,
        title: t("common.tasksClosedByUsers"),
        icon: EntityIcons.Tasks,
        pick: (row) => row.tasksDone,
      });

    default:
      return {
        title: t(`common.${field}`),
        key: `table-${field}`,
        render: () => null,
        data: column,
        ellipsis: true,
        onCell: () => ({ style: { minWidth: getFieldWidth(field) } }),
      };
  }
};

export function createPerUserSummaryReportsTableColumns(
  columns: string[],
  functions: FieldFunctions,
) {
  return columns.map((c) => FieldRenderer(c, functions));
}
